// src/pages/auth/TwoFactorChallengePage.tsx
// OTP input during login.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { authStore } from '@core/stores';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, ErrorAlert } from '@react/components';
import { OtpInput } from '../../components/OtpInput';
import { AuthLayout } from './AuthLayout';

export const TwoFactorChallengePage = observer(() => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [useBackup, setUseBackup] = useState(false);

  const handleVerify = async () => {
    setError(null);
    try {
      await authStore.loginWithTwoFactor(code);
      navigate('/dashboard');
    } catch (err) {
      setError(err);
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle as="h1" className="text-2xl">{t('auth.2faChallengeTitle')}</CardTitle>
          <CardDescription>
            {useBackup ? t('auth.2faEnterBackupCode') : t('auth.2faEnterCode')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}

          {useBackup ? (
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('auth.backupCodePlaceholder')}
              className="w-full px-3 py-2 text-center text-lg font-mono rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          ) : (
            <OtpInput value={code} onChange={setCode} />
          )}
        </CardContent>

        <CardFooter className="flex-col space-y-3">
          <Button fullWidth onClick={handleVerify} isLoading={authStore.isLoading} disabled={code.length < 6}>
            {t('auth.verifyCode')}
          </Button>
          <button
            type="button"
            onClick={() => { setUseBackup(!useBackup); setCode(''); }}
            className="text-sm text-primary hover:text-primary/80"
          >
            {useBackup ? t('auth.useAuthenticator') : t('auth.useBackupCode')}
          </button>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" onClick={() => authStore.cancelTwoFactor()}>
            {t('auth.backToLogin')}
          </Link>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
});

export default TwoFactorChallengePage;
